'use client';

import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import React, { useState } from 'react';
import { Search, Server, Shield, ArrowRight, Send, Lock, Globe } from "lucide-react";
import ClientExecDomainSearch from '@/components/ClientExecDomainSearch';
import { mockDomainSearch } from '@/lib/clientApi';

export default function DomainsPage() {
  const [domainName, setDomainName] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<{
    results: Array<{
      name: string;
      available: boolean;
      price: string | null;
      premium: boolean;
    }>;
    suggestions: Array<{
      name: string;
      available: boolean;
      price: string | null;
      premium: boolean;
    }>;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const domainExtensions = [
    { extension: '.com', price: 16.99, popular: true },
    { extension: '.net', price: 16.99 },
    { extension: '.org', price: 14.99 },
    { extension: '.io', price: 68.99 },
    { extension: '.dev', price: 18.99 },
    { extension: '.co', price: 34.99 },
    { extension: '.me', price: 21.99 },
    { extension: '.app', price: 20.99 },
  ];

  const features = [
    {
      title: 'Free DNS Management',
      description: 'Manage your domain DNS records with our intuitive control panel.',
      icon: <Globe className="w-6 h-6" />,
    },
    {
      title: 'WHOIS Privacy',
      description: 'Keep your personal information private with free WHOIS protection.',
      icon: <Lock className="w-6 h-6" />,
    },
    {
      title: 'Domain Forwarding',
      description: 'Easily forward your domain to any website or landing page.',
      icon: <Server className="w-6 h-6" />,
    },
    {
      title: 'Domain Security',
      description: 'Advanced security features to protect your domain from theft.',
      icon: <Shield className="w-6 h-6" />,
    },
  ];

  const handleSearch = async () => {
    if (!domainName) return;

    setIsSearching(true);
    setError(null);
    setSearchResults(null);

    try {
      await new Promise((r) => setTimeout(r, 400));
      setSearchResults(mockDomainSearch(domainName));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to search domain');
    } finally {
      setIsSearching(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen">
      {/* Hero Section */}
      <section className="relative overflow-hidden pt-48 pb-36 md:pb-36 min-h-[80vh] flex items-center -mt-16">
        {/* Base gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 z-[-1]"></div>
        
        {/* Server rack grid pattern */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[linear-gradient(90deg,transparent_0%,rgba(255,255,255,0.05)_50%,transparent_100%)] bg-[length:4px_100%]"></div>
          <div className="absolute inset-0 bg-[linear-gradient(0deg,transparent_0%,rgba(255,255,255,0.05)_50%,transparent_100%)] bg-[length:100%_4px]"></div>
        </div>

        {/* Animated network connections */}
        <div className="absolute inset-0">
          <div className="absolute h-px w-full top-1/4 left-0 bg-gradient-to-r from-transparent via-primary-500/20 to-transparent animate-pulse"></div>
          <div className="absolute h-px w-full top-2/4 left-0 bg-gradient-to-r from-transparent via-primary-500/20 to-transparent animate-pulse delay-300"></div>
          <div className="absolute h-px w-full top-3/4 left-0 bg-gradient-to-r from-transparent via-primary-500/20 to-transparent animate-pulse delay-700"></div>
        </div>

        {/* Data flow lines */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute w-px h-full left-1/4 top-0 bg-gradient-to-b from-transparent via-primary-500/20 to-transparent animate-network-flow"></div>
          <div className="absolute w-px h-full left-2/4 top-0 bg-gradient-to-b from-transparent via-primary-500/20 to-transparent animate-network-flow delay-300"></div>
          <div className="absolute w-px h-full left-3/4 top-0 bg-gradient-to-b from-transparent via-primary-500/20 to-transparent animate-network-flow delay-700"></div>
        </div>

        {/* Server status indicators */}
        <div className="absolute inset-0">
          <div className="absolute top-10 left-[10%] flex items-center space-x-2">
            <div className="w-2 h-2 rounded-full bg-green-500 animate-ping"></div>
            <div className="w-2 h-2 rounded-full bg-green-500 animate-ping delay-100"></div>
            <div className="w-2 h-2 rounded-full bg-green-500 animate-ping delay-200"></div>
          </div>
          <div className="absolute bottom-10 right-[10%] flex items-center space-x-2">
            <div className="w-2 h-2 rounded-full bg-green-500 animate-ping"></div>
            <div className="w-2 h-2 rounded-full bg-green-500 animate-ping delay-100"></div>
            <div className="w-2 h-2 rounded-full bg-green-500 animate-ping delay-200"></div>
          </div>
        </div>

        {/* Binary data particles */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-full">
            {Array.from({ length: 20 }).map((_, i) => {
              const delayClasses = ['delay-0', 'delay-100', 'delay-200', 'delay-300'];
              const binaryValue = i % 2 === 0 ? '1' : '0';
              return (
                <div
                  key={i}
                  className={`absolute text-primary-500/10 text-xs animate-float-binary ${delayClasses[i % delayClasses.length]}`}
                  style={{
                    left: `${(i % 5) * 25}%`,
                    top: `${Math.floor(i / 5) * 25}%`
                  }}
                >
                  {binaryValue}
                </div>
              );
            })}
          </div>
        </div>

        {/* Animated glowing orbs */}
        <div className="absolute inset-0 overflow-hidden">
          <motion.div
            className="absolute w-64 h-64 rounded-full bg-primary-500/10 blur-3xl"
            animate={{
              x: ['-20%', '120%'],
              y: ['10%', '50%'],
              opacity: [0.1, 0.3, 0.1],
            }}
            transition={{
              duration: 15,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
          <motion.div
            className="absolute w-96 h-96 rounded-full bg-secondary-500/10 blur-3xl"
            animate={{
              x: ['100%', '0%'],
              y: ['80%', '30%'],
              opacity: [0.1, 0.2, 0.1],
            }}
            transition={{
              duration: 20,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 2,
            }}
          />
          <motion.div
            className="absolute w-72 h-72 rounded-full bg-tertiary-500/10 blur-3xl"
            animate={{
              x: ['30%', '70%'],
              y: ['20%', '70%'],
              opacity: [0.1, 0.15, 0.1],
            }}
            transition={{
              duration: 18,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 5,
            }}
          />
        </div>

        {/* Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <span className="inline-block px-4 py-1 rounded-full bg-primary-900/30 text-primary-300 text-sm font-medium mb-6">
                Starting at just $9.99/year
              </span>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-white">
                Find Your Perfect <span className="text-transparent bg-clip-text bg-gradient-rainbow">Domain Name</span>
              </h1>
              
              <p className="text-xl mb-8 text-slate-300">
                Secure your online presence with our wide selection of domain names. Easy search, instant registration.
              </p>

              {/* Domain Search Box */}
              <div className="max-w-2xl mx-auto">
                <ClientExecDomainSearch />

                {/* Search Results - Now handled by ClientExecDomainSearch */}

                {searchResults && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="mt-8 space-y-6"
                  >
                    {/* Main Result */}
                    {searchResults.results.map((result) => (
                      <motion.div
                        key={result.name}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-slate-800/80 border border-slate-700/50 rounded-xl p-6 backdrop-blur-sm"
                      >
                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                          <div>
                            <h3 className="text-xl font-bold text-white">{result.name}</h3>
                            <div className="flex items-center mt-2">
                              {result.available ? (
                                <>
                                  <div className="w-2 h-2 rounded-full bg-green-500 mr-2" />
                                  <span className="text-green-400">Available</span>
                                </>
                              ) : (
                                <>
                                  <div className="w-2 h-2 rounded-full bg-red-500 mr-2" />
                                  <span className="text-red-400">Taken</span>
                                </>
                              )}
                              {result.premium && (
                                <span className="ml-3 px-2 py-1 bg-primary-500/20 text-primary-400 text-xs rounded-full">
                                  Premium
                                </span>
                              )}
                            </div>
                          </div>
                          {result.available && (
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              className="bg-gradient-rainbow text-white px-6 py-3 rounded-lg font-medium shadow-neon flex items-center"
                            >
                              Register for ${result.price ? `$${result.price}` : 'Contact us'}/year
                              <ArrowRight className="ml-2" />
                            </motion.button>
                          )}
                        </div>
                      </motion.div>
                    ))}

                    {/* Suggestions */}
                    {searchResults.suggestions.length > 0 && (
                      <div className="mt-8">
                        <h3 className="text-xl font-semibold text-white mb-4">Similar Domains</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {searchResults.suggestions.map((suggestion) => (
                            <motion.div
                              key={suggestion.name}
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-4 flex items-center justify-between"
                            >
                              <div>
                                <h4 className="text-white font-medium">{suggestion.name}</h4>
                                <div className="flex items-center mt-1">
                                  <div className="w-1.5 h-1.5 rounded-full bg-green-500 mr-2" />
                                  <span className="text-green-400 text-sm">Available</span>
                                </div>
                              </div>
                              <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                className="bg-slate-700 hover:bg-slate-600 text-white px-4 py-2 rounded-lg text-sm flex items-center"
                              >
                                ${suggestion.price ? `$${suggestion.price}` : 'Contact us'}/year
                                <ArrowRight className="ml-2 h-4 w-4" />
                              </motion.button>
                            </motion.div>
                          ))}
                        </div>
                      </div>
                    )}
                  </motion.div>
                )}

                {/* Popular Extensions */}
                {!searchResults && (
                  <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
                    {domainExtensions.slice(0, 4).map((ext, index) => (
                      <motion.div
                        key={ext.extension}
                        className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50 backdrop-blur-sm"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                      >
                        <div className="text-lg font-semibold text-white">{ext.extension}</div>
                        <div className="text-primary-400">${ext.price}/year</div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 to-slate-800"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <motion.span 
              className="inline-block px-4 py-1 rounded-full bg-primary-900/30 text-primary-300 text-sm font-medium mb-4"
              initial={{ opacity: 0, y: -20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              Features
            </motion.span>
            
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Why Choose Our <span className="text-transparent bg-clip-text bg-gradient-rainbow">Domain Service</span>?
            </h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              Get more than just a domain name with our comprehensive domain management features.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                className="bg-slate-800/80 p-8 rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300 border border-slate-700 group"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -10 }}
              >
                <div className="bg-gradient-to-br from-primary-500 to-primary-100 p-4 rounded-full w-16 h-16 flex items-center justify-center mb-6 group-hover:bg-gradient-rainbow transition-all duration-500">
                  <div className="text-primary-500 group-hover:text-white transition-colors duration-300">
                    {feature.icon}
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">{feature.title}</h3>
                <p className="text-slate-300">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Domain Extensions Pricing */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 to-slate-800"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <motion.span 
              className="inline-block px-4 py-1 rounded-full bg-primary-900/30 text-primary-300 text-sm font-medium mb-4"
              initial={{ opacity: 0, y: -20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              Domain Extensions
            </motion.span>
            
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Choose Your <span className="text-transparent bg-clip-text bg-gradient-rainbow">Perfect Domain</span>
            </h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              Wide selection of domain extensions to suit your needs.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {domainExtensions.map((ext, index) => (
              <motion.div
                key={ext.extension}
                className={`relative bg-slate-800/50 rounded-xl border ${ext.popular ? 'border-primary-500/50 shadow-lg shadow-primary-500/10' : 'border-slate-700/50'} overflow-hidden`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ 
                  y: -10, 
                  boxShadow: ext.popular ? '0 25px 50px -12px rgba(99, 102, 241, 0.25)' : '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                  borderColor: 'rgba(99, 102, 241, 0.5)'
                }}
              >
                {ext.popular && (
                  <div className="absolute top-0 right-0">
                    <div className="bg-gradient-rainbow text-white text-sm font-medium px-4 py-1 rounded-bl-lg">
                      Popular
                    </div>
                  </div>
                )}
                
                <div className="p-6">
                  <h3 className="text-2xl font-bold text-white mb-2">{ext.extension}</h3>
                  <div className="flex items-baseline mb-4">
                    <span className="text-4xl font-bold text-white">${ext.price}</span>
                    <span className="text-slate-400 ml-2">/year</span>
                  </div>
                  <motion.div
                    className="mt-6"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Link
                      to="#"
                      className={`block w-full ${ext.popular ? 'bg-gradient-rainbow' : 'bg-slate-700 hover:bg-slate-600'} text-white text-center py-3 px-4 rounded-lg transition-all shadow-lg`}
                    >
                      Register Now
                    </Link>
                  </motion.div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 relative overflow-hidden">
        {/* Background elements - Data center visualization */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 to-slate-800"></div>
        
        {/* Server visualization background */}
        <div className="absolute inset-0">
          {/* Connection lines */}
          <div className="absolute inset-0">
            {[...Array(20)].map((_, i) => (
              <motion.div
                key={`line-${i}`}
                className="absolute bg-primary-600/10 h-[1px]"
                style={{
                  top: `${5 + i * 5}%`,
                  left: '0',
                  right: '0',
                }}
                animate={{
                  opacity: [0.1, 0.3, 0.1],
                }}
                transition={{
                  duration: 3 + (i % 5),
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: i * 0.1,
                }}
              />
            ))}
            
            {[...Array(20)].map((_, i) => (
              <motion.div
                key={`vline-${i}`}
                className="absolute bg-primary-600/10 w-[1px]"
                style={{
                  left: `${5 + i * 5}%`,
                  top: '0',
                  bottom: '0',
                }}
                animate={{
                  opacity: [0.1, 0.2, 0.1],
                }}
                transition={{
                  duration: 4 + (i % 3),
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: i * 0.2,
                }}
              />
            ))}
          </div>
          
          {/* Data nodes */}
          <div className="absolute inset-0">
            {[...Array(30)].map((_, i) => {
              const size = 2 + (i % 3) * 2;
              return (
                <motion.div
                  key={`node-${i}`}
                  className="absolute rounded-full bg-primary-500/30"
                  style={{
                    width: size,
                    height: size,
                    top: `${10 + (i * 73) % 80}%`,
                    left: `${5 + (i * 57) % 90}%`,
                  }}
                  animate={{
                    opacity: [0.2, 0.5, 0.2],
                    scale: [1, 1.5, 1],
                  }}
                  transition={{
                    duration: 3 + (i % 4),
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: i * 0.1,
                  }}
                />
              );
            })}
          </div>
          
          {/* Data flow animations */}
          <div className="absolute inset-0 overflow-hidden">
            {[...Array(15)].map((_, i) => {
              const isVertical = i % 2 === 0;
              const size = isVertical ? '1px' : '2px';
              const startPosition = `${10 + (i * 37) % 80}%`;
              
              return (
                <motion.div
                  key={`flow-${i}`}
                  className="absolute bg-gradient-to-r from-primary-500/0 via-primary-500/50 to-primary-500/0"
                  style={{
                    width: isVertical ? size : '100px',
                    height: isVertical ? '50px' : size,
                    top: isVertical ? '0' : startPosition,
                    left: isVertical ? startPosition : '0',
                  }}
                  animate={{
                    [isVertical ? 'top' : 'left']: ['0%', '100%'],
                    opacity: [0, 1, 0],
                  }}
                  transition={{
                    duration: 4 + (i % 3) * 2,
                    repeat: Infinity,
                    ease: "linear",
                    delay: i * 0.2,
                  }}
                />
              );
            })}
          </div>
          
          {/* Subtle grid overlay */}
          <div className="absolute inset-0 bg-[url('/assets/grid-pattern.svg')] bg-[length:40px_40px] opacity-[0.04]"></div>
          
          {/* Subtle noise texture */}
          <div className="absolute inset-0 bg-[url('/assets/noise.svg')] opacity-[0.02] mix-blend-overlay"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true, margin: "-100px" }}
            >
              <span className="inline-block px-4 py-1 rounded-full bg-primary-900/30 text-primary-300 text-sm font-medium mb-6">
                Get Started Today
              </span>
              
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-white">
                Ready to <span className="text-transparent bg-clip-text bg-gradient-rainbow">Get Started</span>?
              </h2>
              
              <p className="text-xl mb-8 text-slate-300">
                Register your domain name today and start building your online presence.
              </p>
              
              <motion.div
                className="inline-block"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Link
                  to="#"
                  className="bg-gradient-rainbow text-white px-8 py-4 rounded-lg font-medium text-center transition-all shadow-neon inline-flex items-center justify-center"
                >
                  Get Started Now <ArrowRight className="ml-2" />
                </Link>
              </motion.div>
              
              {/* Newsletter Subscription */}
              <motion.div 
                className="mt-12 max-w-md mx-auto"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <h4 className="text-xl font-semibold text-white mb-4">Subscribe to our newsletter</h4>
                <div className="flex flex-col sm:flex-row">
                  <input 
                    type="email" 
                    placeholder="Your email" 
                    className="bg-slate-800/80 text-white rounded-l-lg sm:rounded-r-none rounded-r-lg sm:rounded-b-none rounded-b-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-500 w-full border border-slate-700 mb-2 sm:mb-0"
                  />
                  <motion.button
                    className="bg-gradient-rainbow text-white px-6 py-3 rounded-l-lg sm:rounded-l-none rounded-r-lg font-medium flex items-center justify-center"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Send className="mr-2" />
                    <span>Subscribe</span>
                  </motion.button>
                </div>
                <p className="mt-3 text-xs text-slate-400">
                  By subscribing, you agree to our Privacy Policy and consent to receive updates.
                </p>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}